import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Chip,
  Slider,
  Collapse,
  IconButton,
  Table,
  TableBody,
  TableRow,
  TableCell,
} from '@mui/material';
import {
  Phone as CallIcon,
  CallEnd as HangUpIcon,
  Backspace as BackspaceIcon,
  Mic as MicIcon,
  MicOff as MicOffIcon,
  Pause as PauseIcon,
  PlayArrow as PlayIcon,
  Dialpad as DialpadIcon,
  VolumeUp as VolumeUpIcon,
  Analytics as AnalyticsIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import { useWebPhoneContext } from '../../contexts/WebPhoneContext';

const WEBPHONE_VERSION = '1.00.28';

const WebPhoneStandalone: React.FC = () => {
  const {
    connectionStatus,
    callStatus,
    currentCall,
    isMuted,
    isOnHold,
    dialedNumber,
    errorMessage,
    volume,
    makeCall,
    answerCall,
    hangUp,
    toggleMute,
    toggleHold,
    addDigit,
    clearNumber,
    changeVolume,
    testSpeaker,
    connect,
    rtcStats,
    eventLogs,
  } = useWebPhoneContext();

  const [showDialpad, setShowDialpad] = useState(true);
  const [showStats, setShowStats] = useState(true);

  // Auto-connect on mount
  useEffect(() => {
    if (connectionStatus === 'disconnected') {
      connect();
    }
  }, []);

  // Format call duration
  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Get status display
  const getStatusDisplay = () => {
    if (connectionStatus === 'disconnected') return { text: '🔴 Disconnected', color: '#f44336' };
    if (connectionStatus === 'connecting') return { text: '🟡 Connecting...', color: '#ff9800' };
    if (connectionStatus === 'connected') return { text: '🟢 Connected', color: '#4caf50' };
    if (connectionStatus === 'registered') return { text: '✅ REGISTERED - Ready to Call', color: '#4caf50' };
    if (connectionStatus === 'error') return { text: '🔴 Error', color: '#f44336' };
    return { text: 'Unknown', color: '#9e9e9e' };
  };

  const getCallStatusDisplay = () => {
    if (callStatus === 'calling') return { text: '📞 Calling...', color: '#ff9800' };
    if (callStatus === 'ringing') return { text: '📞 Ringing...', color: '#ff9800' };
    if (callStatus === 'in-call') return { text: '☎️ In Call', color: '#2196f3' };
    return null;
  };

  const status = getStatusDisplay();
  const callStatusInfo = getCallStatusDisplay();

  // Dialpad keys
  const dialpadKeys = [
    { digit: '1', letters: '' },
    { digit: '2', letters: 'ABC' },
    { digit: '3', letters: 'DEF' },
    { digit: '4', letters: 'GHI' },
    { digit: '5', letters: 'JKL' },
    { digit: '6', letters: 'MNO' },
    { digit: '7', letters: 'PQRS' },
    { digit: '8', letters: 'TUV' },
    { digit: '9', letters: 'WXYZ' },
    { digit: '*', letters: '' },
    { digit: '0', letters: '+' },
    { digit: '#', letters: '' },
  ];

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.default',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          p: 2,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <CallIcon sx={{ mr: 1 }} />
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          WebPhone
        </Typography>
      </Box>

      {/* Body */}
      <Box sx={{ p: 2, flex: 1, overflow: 'auto' }}>
        {/* Status Display */}
        <Chip
          label={callStatusInfo ? callStatusInfo.text : status.text}
          size="small"
          sx={{
            width: '100%',
            mb: 2,
            fontWeight: 600,
            bgcolor: callStatusInfo ? callStatusInfo.color : status.color,
            color: 'white',
          }}
        />

        {/* Error Message */}
        {errorMessage && (
          <Box sx={{ mb: 2, p: 1, bgcolor: '#fee', borderRadius: 1 }}>
            <Typography variant="body2" color="error">
              {errorMessage}
            </Typography>
          </Box>
        )}

        {/* Volume Control - Always visible */}
        <Box sx={{ mb: 2, p: 2, bgcolor: '#f8f9fa', borderRadius: 1 }}>
          <Typography variant="caption" sx={{ color: '#666', mb: 0.5, display: 'block' }}>
            Volume: {volume}%
          </Typography>
          <Slider
            value={volume}
            onChange={(_, newValue) => changeVolume(newValue as number)}
            min={0}
            max={100}
            step={5}
            size="small"
            sx={{
              color: '#667eea',
              mb: 1,
            }}
          />
          <Button
            size="small"
            variant="outlined"
            onClick={testSpeaker}
            fullWidth
            startIcon={<VolumeUpIcon fontSize="small" />}
            sx={{
              fontSize: '0.75rem',
              py: 0.5,
              textTransform: 'none',
            }}
          >
            Test Speaker
          </Button>
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              textAlign: 'center',
              color: '#999',
              fontSize: '0.65rem',
              mt: 1,
            }}
          >
            Version {WEBPHONE_VERSION}
          </Typography>
        </Box>

        {/* RTC Statistics Panel */}
        <Box sx={{ mb: 2 }}>
          <Button
            size="small"
            variant="outlined"
            onClick={() => setShowStats(!showStats)}
            fullWidth
            startIcon={<AnalyticsIcon fontSize="small" />}
            endIcon={showStats ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
            sx={{
              fontSize: '0.75rem',
              py: 0.5,
              textTransform: 'none',
              borderColor: '#667eea',
              color: '#667eea',
            }}
          >
            {showStats ? 'Hide' : 'Show'} Call Statistics
          </Button>
          <Collapse in={showStats}>
            <Paper variant="outlined" sx={{ mt: 1, p: 1.5, bgcolor: '#fafbfc', borderColor: '#e1e4e8' }}>
              {/* Event Log */}
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#667eea', mb: 1, display: 'block' }}>
                Event Log
              </Typography>
              <Box sx={{
                maxHeight: 200,
                overflowY: 'auto',
                bgcolor: '#f5f5f5',
                borderRadius: 1,
                p: 1,
                mb: 2,
                fontFamily: 'monospace',
                fontSize: '0.7rem'
              }}>
                {eventLogs && eventLogs.length > 0 ? (
                  eventLogs.slice().reverse().map((log, index) => (
                    <Box key={index} sx={{
                      mb: 0.5,
                      color: log.type === 'error' ? '#f44336' : log.type === 'success' ? '#4caf50' : log.type === 'warning' ? '#ff9800' : '#666'
                    }}>
                      <span style={{ color: '#999', marginRight: '8px' }}>[{log.timestamp}]</span>
                      {log.message}
                    </Box>
                  ))
                ) : (
                  <Box sx={{ color: '#999', textAlign: 'center', py: 1 }}>
                    No events yet. Make a call to see events.
                  </Box>
                )}
              </Box>

              {/* RTC Statistics */}
              {rtcStats ? (
                <>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: '#667eea', mb: 1, display: 'block' }}>
                    Audio Stream
                  </Typography>
                  <Table size="small" sx={{ '& td, & th': { fontSize: '0.7rem', py: 0.3, border: 'none' } }}>
                    <TableBody>
                      <TableRow>
                        <TableCell sx={{ color: '#666', fontWeight: 500 }}>Receive:</TableCell>
                        <TableCell sx={{ textAlign: 'right', color: '#333' }}>{rtcStats.receiveBitrate} kbps</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ color: '#666', fontWeight: 500 }}>Send:</TableCell>
                        <TableCell sx={{ textAlign: 'right', color: '#333' }}>{rtcStats.sendBitrate} kbps</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ color: '#666', fontWeight: 500 }}>Packets RX:</TableCell>
                        <TableCell sx={{ textAlign: 'right', color: '#333' }}>{rtcStats.packetsReceived.toLocaleString()}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ color: '#666', fontWeight: 500 }}>Packets TX:</TableCell>
                        <TableCell sx={{ textAlign: 'right', color: '#333' }}>{rtcStats.packetsSent.toLocaleString()}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ color: '#666', fontWeight: 500 }}>Packet Loss:</TableCell>
                        <TableCell sx={{ textAlign: 'right', color: rtcStats.packetsLost > 0 ? '#f44336' : '#4caf50' }}>
                          {rtcStats.packetsLostPercentage}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>

                  <Typography variant="caption" sx={{ fontWeight: 600, color: '#667eea', mt: 1.5, mb: 0.5, display: 'block' }}>
                    Network Quality
                  </Typography>
                  <Table size="small" sx={{ '& td, & th': { fontSize: '0.7rem', py: 0.3, border: 'none' } }}>
                    <TableBody>
                      <TableRow>
                        <TableCell sx={{ color: '#666', fontWeight: 500 }}>Jitter:</TableCell>
                        <TableCell sx={{ textAlign: 'right', color: '#333' }}>{rtcStats.jitter}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ color: '#666', fontWeight: 500 }}>RTT:</TableCell>
                        <TableCell sx={{ textAlign: 'right', color: '#333' }}>{rtcStats.roundTripTime}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ color: '#666', fontWeight: 500 }}>Codec:</TableCell>
                        <TableCell sx={{ textAlign: 'right', color: '#333' }}>{rtcStats.codec.replace('audio/', '')}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>

                  <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', color: '#999', fontSize: '0.6rem', mt: 1 }}>
                    Updated every 2 seconds
                  </Typography>
                </>
              ) : (
                <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', color: '#999', py: 2 }}>
                  Statistics will appear during an active call
                </Typography>
              )}
            </Paper>
          </Collapse>
        </Box>

        {/* Call Info (when in call) */}
        {currentCall && (
          <Box sx={{ mb: 2, textAlign: 'center' }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {currentCall.remoteNumber}
            </Typography>
            {currentCall.status === 'connected' && (
              <Typography variant="body2" color="text.secondary">
                {formatDuration(currentCall.duration)}
              </Typography>
            )}
          </Box>
        )}

        {/* Number Display */}
        <Paper
          variant="outlined"
          sx={{
            p: 2,
            mb: 2,
            minHeight: 60,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            bgcolor: '#f8f9fa',
            borderColor: '#e9ecef',
          }}
        >
          <Typography
            variant="h5"
            sx={{
              fontWeight: 600,
              color: dialedNumber ? '#333' : '#adb5bd',
              wordBreak: 'break-all',
            }}
          >
            {dialedNumber || 'Enter number'}
          </Typography>
        </Paper>

        {/* Dialpad (show only when not in call or if toggled) */}
        {(callStatus === 'idle' || showDialpad) && (
          <Grid container spacing={1} sx={{ mb: 2 }}>
            {dialpadKeys.map((key) => (
              <Grid item xs={4} key={key.digit}>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={() => addDigit(key.digit)}
                  sx={{
                    height: 60,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0.5,
                    bgcolor: '#f8f9fa',
                    borderColor: '#e9ecef',
                    '&:hover': {
                      bgcolor: '#e9ecef',
                      transform: 'scale(1.05)',
                    },
                  }}
                >
                  <Typography variant="h5" sx={{ fontWeight: 600, color: '#333' }}>
                    {key.digit}
                  </Typography>
                  {key.letters && (
                    <Typography variant="caption" sx={{ color: '#666', fontSize: 10 }}>
                      {key.letters}
                    </Typography>
                  )}
                </Button>
              </Grid>
            ))}
          </Grid>
        )}

        {/* Action Buttons */}
        <Grid container spacing={1}>
          {callStatus === 'idle' && (
            <>
              <Grid item xs={6}>
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<CallIcon />}
                  onClick={() => makeCall(dialedNumber)}
                  disabled={connectionStatus !== 'registered' || !dialedNumber || callStatus !== 'idle'}
                  sx={{
                    bgcolor: '#4caf50',
                    '&:hover': { bgcolor: '#45a049' },
                  }}
                >
                  Call
                </Button>
              </Grid>
              <Grid item xs={6}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<BackspaceIcon />}
                  onClick={clearNumber}
                >
                  Clear
                </Button>
              </Grid>
            </>
          )}

          {callStatus === 'calling' && (
            <Grid item xs={12}>
              <Button
                fullWidth
                variant="contained"
                startIcon={<HangUpIcon />}
                onClick={hangUp}
                sx={{ bgcolor: '#f44336', '&:hover': { bgcolor: '#da190b' } }}
              >
                Cancel
              </Button>
            </Grid>
          )}

          {callStatus === 'ringing' && currentCall?.direction === 'incoming' && (
            <>
              <Grid item xs={6}>
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<CallIcon />}
                  onClick={answerCall}
                  sx={{ bgcolor: '#4caf50', '&:hover': { bgcolor: '#45a049' } }}
                >
                  Answer
                </Button>
              </Grid>
              <Grid item xs={6}>
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<HangUpIcon />}
                  onClick={hangUp}
                  sx={{ bgcolor: '#f44336', '&:hover': { bgcolor: '#da190b' } }}
                >
                  Decline
                </Button>
              </Grid>
            </>
          )}

          {callStatus === 'in-call' && (
            <>
              <Grid item xs={6}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={isMuted ? <MicOffIcon /> : <MicIcon />}
                  onClick={toggleMute}
                  sx={{
                    bgcolor: isMuted ? '#ff9800' : 'transparent',
                    color: isMuted ? 'white' : 'inherit',
                  }}
                >
                  {isMuted ? 'Unmute' : 'Mute'}
                </Button>
              </Grid>
              <Grid item xs={6}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={isOnHold ? <PlayIcon /> : <PauseIcon />}
                  onClick={toggleHold}
                  sx={{
                    bgcolor: isOnHold ? '#9c27b0' : 'transparent',
                    color: isOnHold ? 'white' : 'inherit',
                  }}
                >
                  {isOnHold ? 'Resume' : 'Hold'}
                </Button>
              </Grid>
              <Grid item xs={6}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<DialpadIcon />}
                  onClick={() => setShowDialpad(!showDialpad)}
                >
                  {showDialpad ? 'Hide' : 'Show'} Keys
                </Button>
              </Grid>
              <Grid item xs={6}>
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<HangUpIcon />}
                  onClick={hangUp}
                  sx={{ bgcolor: '#f44336', '&:hover': { bgcolor: '#da190b' } }}
                >
                  Hang Up
                </Button>
              </Grid>
            </>
          )}
        </Grid>
      </Box>
    </Box>
  );
};

export default WebPhoneStandalone;
